/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { RecordReviewSchema, type RecordReviewInput } from '@therapy/shared-validators';
import { type SyntheticEvent, useCallback, type JSX } from 'react';
import { type FieldErrors, type UseFormRegister, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button.js';
import { Modal } from '../../components/ui/modal.js';
import { useRecordReview } from '../../lib/queries/reviews.js';

interface RecordReviewModalProps {
  readonly planId: string;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly defaultNextDate: string;
  readonly onSaved: () => void;
}

const OUTCOMES = ['continue', 'revise', 'discharge'] as const;
const LABEL = 'mb-1.5 block text-sm font-medium text-slate-700';
const FIELD = 'focus-ring w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm';

interface FieldsProps {
  readonly register: UseFormRegister<RecordReviewInput>;
  readonly errors: FieldErrors<RecordReviewInput>;
  readonly isDischarge: boolean;
}

/** The review form fields (kept separate so the modal stays within the line cap). */
function ReviewFields({ register, errors, isDischarge }: FieldsProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      <div>
        <label htmlFor="review-outcome" className={LABEL}>
          {t('therapist.review.outcome')}
        </label>
        <select id="review-outcome" className={FIELD} {...register('outcome')}>
          {OUTCOMES.map((outcome) => (
            <option key={outcome} value={outcome}>
              {t(`therapist.review.outcome_${outcome}`)}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label htmlFor="review-summary" className={LABEL}>
          {t('therapist.review.summary')}
        </label>
        <textarea
          id="review-summary"
          rows={3}
          aria-invalid={errors.summary !== undefined}
          className={FIELD}
          {...register('summary')}
        />
        {errors.summary?.message !== undefined && (
          <p role="alert" className="mt-1 text-xs text-red-600">
            {errors.summary.message}
          </p>
        )}
      </div>
      <div>
        <label htmlFor="review-recommendations" className={LABEL}>
          {t('therapist.review.recommendations')}
        </label>
        <textarea
          id="review-recommendations"
          rows={2}
          className={FIELD}
          {...register('recommendations')}
        />
      </div>
      {!isDischarge && (
        <div>
          <label htmlFor="review-next-date" className={LABEL}>
            {t('therapist.review.nextDate')}
          </label>
          <input
            id="review-next-date"
            type="date"
            aria-invalid={errors.nextReviewDate !== undefined}
            className={FIELD}
            {...register('nextReviewDate')}
          />
          {errors.nextReviewDate?.message !== undefined && (
            <p role="alert" className="mt-1 text-xs text-red-600">
              {errors.nextReviewDate.message}
            </p>
          )}
        </div>
      )}
    </>
  );
}

/** Record-review dialog (BRD M01) — captures the periodic review + rolls the plan forward. */
export function RecordReviewModal({
  planId,
  open,
  onOpenChange,
  defaultNextDate,
  onSaved,
}: RecordReviewModalProps): JSX.Element {
  const { t } = useTranslation();
  const record = useRecordReview(planId);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<RecordReviewInput>({
    resolver: zodResolver(RecordReviewSchema),
    defaultValues: {
      outcome: 'continue',
      summary: '',
      recommendations: '',
      nextReviewDate: defaultNextDate,
    },
  });

  const submit = useCallback(
    async (values: RecordReviewInput): Promise<void> => {
      await record.mutateAsync(values);
      onSaved();
    },
    [record, onSaved],
  );
  const onFormSubmit = useCallback(
    (event: SyntheticEvent<HTMLFormElement>): void => {
      void handleSubmit(submit)(event);
    },
    [handleSubmit, submit],
  );

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t('therapist.review.modalTitle')}
      description={t('therapist.review.modalSubtitle')}
      closeLabel={t('common.cancel')}
    >
      <form onSubmit={onFormSubmit} className="space-y-4" noValidate>
        <ReviewFields
          register={register}
          errors={errors}
          isDischarge={watch('outcome') === 'discharge'}
        />
        {record.isError && (
          <p role="alert" className="text-sm text-red-600">
            {t('common.saveError')}
          </p>
        )}
        <div className="flex justify-end gap-3 pt-1">
          <Button type="button" variant="outline" onClick={onSaved}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={isSubmitting || record.isPending}>
            {t('therapist.review.submit')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
